import { useState } from "react";
import {
  CheckCircle2,
  Clock,
  Instagram,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Navigation,
  Phone,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/Reveal";
import { CONTACTO, HORARIO, SOCIAL } from "@/constants/site";
import { enviarContacto } from "@/lib/api";

const VACIO = { nombre: "", email: "", telefono: "", mensaje: "" };

export const Ubicacion = () => {
  const [form, setForm] = useState(VACIO);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      await enviarContacto(form);
      setStatus("success");
      setForm(VACIO);
    } catch {
      setStatus("error");
      setErrorMsg("No se pudo enviar el mensaje. Revisa los campos e inténtalo de nuevo.");
    }
  };

  const info = [
    {
      icon: MapPin,
      label: "Dirección",
      valor: `${CONTACTO.direccion1} · ${CONTACTO.direccion2}`,
      testid: "location-address",
    },
    {
      icon: Phone,
      label: "Teléfono",
      valor: CONTACTO.telefono,
      href: CONTACTO.telefonoHref,
      testid: "location-phone",
    },
    {
      icon: Mail,
      label: "Email",
      valor: CONTACTO.email,
      href: CONTACTO.emailHref,
      testid: "location-email",
    },
    {
      icon: Instagram,
      label: "Instagram",
      valor: "@pastisseria_rawan_",
      href: SOCIAL.instagram,
      externo: true,
      testid: "location-instagram",
    },
    {
      icon: MessageCircle,
      label: "WhatsApp",
      valor: CONTACTO.telefono,
      href: SOCIAL.whatsapp,
      externo: true,
      testid: "location-whatsapp",
    },
  ];

  return (
    <section id="ubicacion" data-testid="location-section" className="pb-20 sm:pb-28 lg:pb-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-cafe">
            <span aria-hidden className="h-px w-9 bg-cafe/60" />
            Ubicación y contacto
          </p>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl leading-[1.14] tracking-tight text-tinta sm:text-4xl lg:text-[2.9rem]">
            Ven a conocernos,{" "}
            <em className="font-garamond font-medium italic text-cafe">te esperamos.</em>
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal delay={0.1}>
            <ul className="space-y-7">
              {info.map(({ icon: Icon, label, valor, href, externo, testid }) => (
                <li key={label} data-testid={testid} className="flex items-start gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-arena/60">
                    <Icon className="h-5 w-5 text-chocolate" strokeWidth={1.8} />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-tinta/50">
                      {label}
                    </span>
                    {href ? (
                      <a
                        href={href}
                        data-testid={`${testid}-link`}
                        {...(externo ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="mt-1 block break-all font-medium text-tinta transition-colors hover:text-cafe"
                      >
                        {valor}
                      </a>
                    ) : (
                      <span className="mt-1 block font-medium text-tinta">{valor}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            <div
              data-testid="location-hours"
              className="mt-8 rounded-3xl border border-arena/70 bg-white p-6 sm:p-8"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-arena/60">
                  <Clock className="h-5 w-5 text-chocolate" strokeWidth={1.8} />
                </span>
                <h3 className="font-serif text-xl text-tinta sm:text-2xl">Horario</h3>
              </div>
              <dl className="mt-5 space-y-3">
                {HORARIO.map((fila) => (
                  <div
                    key={fila.dias}
                    data-testid={`hours-row-${fila.dias.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex flex-col gap-0.5 border-b border-arena/50 pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
                  >
                    <dt className="text-sm font-semibold text-tinta">{fila.dias}</dt>
                    <dd
                      className={`text-sm ${
                        fila.franjas === "Cerrado" ? "font-medium text-cafe" : "text-tinta/70"
                      }`}
                    >
                      {fila.franjas}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <Button
              variant="outline"
              asChild
              className="mt-8 rounded-full px-7 py-6 text-base"
            >
              <a
                data-testid="location-directions-button"
                href={CONTACTO.comoLlegarUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Navigation className="h-4 w-4" /> Cómo llegar
              </a>
            </Button>
          </Reveal>

          <Reveal delay={0.2}>
            <div
              data-testid="contact-form"
              className="rounded-3xl border border-arena/70 bg-white p-6 shadow-[0_18px_40px_rgba(58,36,24,0.08)] sm:p-8"
            >
              {status === "success" ? (
                <div
                  data-testid="contact-form-success"
                  className="flex h-full min-h-[380px] flex-col items-center justify-center text-center"
                >
                  <CheckCircle2 className="h-14 w-14 text-green-700" strokeWidth={1.4} />
                  <h3 className="mt-6 font-serif text-2xl text-tinta">Mensaje enviado</h3>
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-tinta/65">
                    Gracias por escribirnos. Te responderemos lo antes posible.
                  </p>
                  <Button
                    variant="outline"
                    data-testid="contact-form-success-reset"
                    onClick={() => setStatus("idle")}
                    className="mt-8 rounded-full px-7"
                  >
                    Enviar otro mensaje
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" noValidate={false}>
                  <h3 className="font-serif text-2xl text-tinta">Escríbenos</h3>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="cf-nombre">Nombre</Label>
                      <Input
                        id="cf-nombre"
                        data-testid="contact-form-nombre"
                        value={form.nombre}
                        onChange={set("nombre")}
                        placeholder="Tu nombre"
                        required
                        minLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cf-email">Email</Label>
                      <Input
                        id="cf-email"
                        data-testid="contact-form-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="tu@email.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf-telefono">Teléfono</Label>
                    <Input
                      id="cf-telefono"
                      data-testid="contact-form-telefono"
                      type="tel"
                      value={form.telefono}
                      onChange={set("telefono")}
                      placeholder="+34 600 000 000"
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cf-mensaje">Mensaje</Label>
                    <Textarea
                      id="cf-mensaje"
                      data-testid="contact-form-mensaje"
                      value={form.mensaje}
                      onChange={set("mensaje")}
                      placeholder="Cuéntanos en qué podemos ayudarte"
                      rows={4}
                      required
                      minLength={10}
                    />
                  </div>
                  {status === "error" && (
                    <p
                      data-testid="contact-form-error"
                      role="alert"
                      className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
                    >
                      {errorMsg}
                    </p>
                  )}
                  <Button
                    type="submit"
                    data-testid="contact-form-submit"
                    disabled={status === "sending"}
                    className="w-full rounded-full py-6 text-base sm:w-auto sm:px-10"
                  >
                    {status === "sending" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Enviar mensaje
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-12">
          <div
            data-testid="location-map"
            className="relative h-[360px] overflow-hidden rounded-3xl border border-arena shadow-sm"
          >
            <iframe
              title="Mapa — Pastelería Panadería Rawan, Manlleu"
              loading="lazy"
              className="h-full w-full"
              src={CONTACTO.mapEmbedUrl}
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Ubicacion;
